import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Palette, Eye, Check, X, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/brand-requests${params}`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      // error handled silently
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/brand-requests/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success(`Request ${status}`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this brand request? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/brand-requests/${id}`, { withCredentials: true });
      toast.success('Request deleted');
      fetchRequests();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-1 rounded-sm">Pending</span>;
      case 'approved': return <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-sm">Approved</span>;
      case 'rejected': return <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-sm">Rejected</span>;
      default: return <span className="bg-slate-100 text-slate-700 text-xs font-semibold px-2 py-1 rounded-sm">{status}</span>;
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="brand-requests">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Brand Requests</h1>
          <p className="text-slate-600 mt-1">Review logo and brand asset requests</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="status-filter">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Requests</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Requests List */}
      <div className="bg-white border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 font-semibold text-[#002249]">Requester</th>
                <th className="text-left p-4 font-semibold text-[#002249] hidden md:table-cell">Company</th>
                <th className="text-left p-4 font-semibold text-[#002249] hidden lg:table-cell">Intended Use</th>
                <th className="text-left p-4 font-semibold text-[#002249]">Status</th>
                <th className="text-right p-4 font-semibold text-[#002249]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {requests.map((request, index) => (
                <tr key={request.id} className="hover:bg-slate-50" data-testid={`brand-request-row-${index}`}>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-[#002249] rounded-sm flex items-center justify-center flex-shrink-0 text-white">
                        <Palette className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium text-[#002249]">{request.name}</p>
                        <p className="text-xs text-slate-500">{request.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell text-sm text-slate-700">{request.company}</td>
                  <td className="p-4 hidden lg:table-cell text-sm text-slate-600 max-w-xs truncate">{request.intended_use}</td>
                  <td className="p-4">{getStatusBadge(request.status)}</td>
                  <td className="p-4">
                    <div className="flex justify-end space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(request)} data-testid={`view-brand-request-${index}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {request.status === 'pending' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => updateStatus(request.id, 'approved')} className="text-green-600 hover:text-green-700" data-testid={`approve-brand-request-${index}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => updateStatus(request.id, 'rejected')} className="text-red-500 hover:text-red-700" data-testid={`reject-brand-request-${index}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(request.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-brand-request-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {requests.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              <Palette className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p>No brand requests found</p>
            </div>
          )}
        </div>
      </div>

      {/* Request Details Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Brand Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-[#002249]">Logo Usage Request</span>
                {getStatusBadge(selectedRequest.status)}
              </div>
              <div className="bg-slate-50 p-4 rounded-sm grid md:grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-slate-500 block">Name</span>
                  <span className="font-medium">{selectedRequest.name}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Company</span>
                  <span className="font-medium">{selectedRequest.company}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Email</span>
                  <span>{selectedRequest.email}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block">Phone</span>
                  <span>{selectedRequest.phone}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-slate-500 block mb-1">Intended Use</span>
                  <p className="bg-slate-50 p-3 rounded-sm text-sm">{selectedRequest.intended_use}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 block mb-1">Where Logo Will Appear</span>
                  <p className="bg-slate-50 p-3 rounded-sm text-sm">{selectedRequest.where_appear}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <span className={selectedRequest.acknowledged_disclaimer ? 'text-green-600' : 'text-red-500'}>
                  {selectedRequest.acknowledged_disclaimer ? '✓ Disclaimer acknowledged' : '✗ Disclaimer not acknowledged'}
                </span>
              </div>
              {selectedRequest.status === 'pending' && (
                <div className="flex space-x-3 pt-4">
                  <Button onClick={() => { updateStatus(selectedRequest.id, 'approved'); setSelectedRequest(null); }} className="flex-1 bg-green-600 hover:bg-green-700 rounded-sm">
                    <Check className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button onClick={() => { updateStatus(selectedRequest.id, 'rejected'); setSelectedRequest(null); }} variant="outline" className="flex-1 border-red-500 text-red-500 hover:bg-red-50 rounded-sm">
                    <X className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandRequestsPage;
